import React, { useEffect, useState, useCallback, useRef } from 'react';
import { message, Select } from 'antd';
import { FutureInstrument } from '../../../types';
import CandlestickChart, { Candle, ChartPriceLine } from '../../../components/CandlestickChart';
import apiService, { ServiceType } from '../../../services/api';

const coreService = apiService.getService(ServiceType.CORE);

export const CHART_INTERVAL_OPTIONS = [
  { value: '1min', label: '1 min' },
  { value: '5min', label: '5 min' },
  { value: '1h', label: '1 hour' },
  { value: '1day', label: '1 day' },
  { value: '1w', label: '1 week' },
] as const;

export type ChartInterval = typeof CHART_INTERVAL_OPTIONS[number]['value'];

interface ChartSectionProps {
  /** Only the symbol is read, so the Price Board can pass a bare `{ symbol }`. */
  selectedInstrument: Pick<FutureInstrument, 'symbol'> | null;
  priceData?: any;
  priceLines?: ChartPriceLine[];
}

const ChartSection: React.FC<ChartSectionProps> = ({
  selectedInstrument,
  priceData,
  priceLines,
}) => {
  const [ohlcCandles, setOhlcCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState(false);
  const [interval, setInterval] = useState<ChartInterval>('1day');
  const nextCursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);

  const fetchOhlcData = useCallback(async (symbol: string, timeScale: ChartInterval) => {
    try {
      setLoading(true);
      nextCursorRef.current = null;
      const response = await coreService.get('/price/chart', {
        params: { tickerSymbol: symbol, timeScale },
        timeout: 15000,
      });

      const data = response.data;
      if (data?.ohlc && Array.isArray(data.ohlc)) {
        const candles: Candle[] = data.ohlc.map((c: any) => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
          volume: c.volume || 0
        }));
        setOhlcCandles(candles);
        nextCursorRef.current = data.nextCursor || null;
      } else {
        setOhlcCandles([]);
      }
    } catch (err: any) {
      console.error('Error fetching OHLC data:', err);
      message.error('Failed to load chart data');
      setOhlcCandles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (loadingMoreRef.current || !nextCursorRef.current || !selectedInstrument?.symbol) return;
    loadingMoreRef.current = true;
    try {
      const response = await coreService.get('/price/chart', {
        params: {
          tickerSymbol: selectedInstrument.symbol,
          timeScale: interval,
          cursor: nextCursorRef.current,
        }
      });
      const data = response.data;
      if (data?.ohlc && Array.isArray(data.ohlc)) {
        const olderCandles: Candle[] = data.ohlc.map((c: any) => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
          volume: c.volume || 0
        }));
        if (olderCandles.length > 0) {
          setOhlcCandles(prev => [...olderCandles, ...prev]);
        }
        nextCursorRef.current = data.nextCursor || null;
      }
    } catch (err) {
      console.error('Error loading more OHLC data:', err);
    } finally {
      loadingMoreRef.current = false;
    }
  }, [selectedInstrument?.symbol, interval]);

  useEffect(() => {
    if (selectedInstrument?.symbol) {
      setOhlcCandles([]);
      fetchOhlcData(selectedInstrument.symbol, interval);
    }
  }, [selectedInstrument?.symbol, interval, fetchOhlcData]);

  // Always render the chart canvas — even with no instrument selected or no data,
  // the empty grid (price/time axes) shows instead of a blank/null placeholder.
  return (
    <CandlestickChart
      candles={ohlcCandles}
      symbol={selectedInstrument?.symbol ?? ''}
      height={0}
      interval={interval}
      priceData={priceData}
      loading={loading}
      priceLines={priceLines}
      onLoadMore={handleLoadMore}
      priceDivisor={1}
      headerExtra={
        <Select
          size="small"
          value={interval}
          onChange={(v) => setInterval(v as ChartInterval)}
          options={[...CHART_INTERVAL_OPTIONS]}
          style={{ width: 90 }}
        />
      }
    />
  );
};

export default ChartSection;
